import { Injectable, signal, computed } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Service de chargement global.
 *
 * Utilise un compteur de requêtes (au lieu d'un boolean simple) pour
 * gérer correctement plusieurs requêtes HTTP simultanées : le loader
 * ne se cache que lorsque TOUTES les requêtes en cours sont terminées.
 *
 * Expose à la fois :
 *  - un signal Angular `isLoading` (consommable via `loaderService.isLoading()`)
 *  - un Observable `loading$` (rétrocompat : `loaderService.loading$ | async`)
 */
@Injectable({ providedIn: 'root' })
export class LoaderService {
  /** Compteur des requêtes en cours. */
  private pendingCount = signal(0);

  /** Signal public : true tant qu'au moins une requête tourne. */
  isLoading = computed(() => this.pendingCount() > 0);

  /** Stream Observable conservé pour rétrocompatibilité (`| async`). */
  private _loadingSubject = new BehaviorSubject<boolean>(false);
  loading$: Observable<boolean> = this._loadingSubject.asObservable();

  show(): void {
    this.pendingCount.update(n => n + 1);
    this._loadingSubject.next(true);
  }

  hide(): void {
    this.pendingCount.update(n => Math.max(0, n - 1));
    if (this.pendingCount() === 0) {
      this._loadingSubject.next(false);
    }
  }

  /** Reset complet (utile en cas d'erreur globale). */
  reset(): void {
    this.pendingCount.set(0);
    this._loadingSubject.next(false);
  }

  /**
   * Force le masquage immédiat du loader, peu importe le compteur.
   * À utiliser quand on sait que la partie critique de la page est prête
   * (ex: hero rendu) et qu'on veut laisser le reste se charger en arrière-plan.
   */
  forceHide(): void {
    this.pendingCount.set(0);
    this._loadingSubject.next(false);
  }
}
